import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'store'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import ModalBig from 'components/common/modalBig'
import {
  calcApi,
  refetchOrderAfterDocumentApply,
  useApplyOrderDocumentDraftMutation,
  useExtractOrderDocumentMutation,
  useLazyOrderDocumentByIdQuery,
  useUploadOrderDocumentMutation,
} from 'api/calc'
import { COLORS } from 'config/constants'
import { useGetCalcIdFromRoute } from 'utils/hooks'

import { pollOrderDocumentUntilReady } from './poll-document'
import DocumentImportProductEditor, { parseProductFieldValue } from './document-import-product-editor'
import { normalizeDraftProduct } from './normalize-draft-product'
import { buildReadiness } from './readiness'

import './document-import.scss'

type Step = 'upload' | 'processing' | 'edit' | 'error'

type Props = {
  isOpen: boolean
  onClose: () => void
  onApplied?: () => void
}

const ACCEPT = '.pdf,.xls,.xlsx,.csv,.txt'

const DocumentImportModal = ({ isOpen, onClose, onApplied }: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const orderId = useGetCalcIdFromRoute() || ''

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [ step, setStep ] = useState<Step>('upload')
  const [ docKind, setDocKind ] = useState<CalculationDocumentDocKind>('invoice')
  const [ selectedFile, setSelectedFile ] = useState<File | null>(null)
  const [ documentId, setDocumentId ] = useState<number | null>(null)
  const [ invoiceDraft, setInvoiceDraft ] = useState<CalculationDocumentDraftInvoice>({})
  const [ productsDraft, setProductsDraft ] = useState<CalculationDocumentDraftProduct[]>([])
  const [ draftDocKind, setDraftDocKind ] = useState<CalculationDocumentDocKind>('invoice')
  const [ warnings, setWarnings ] = useState<string[]>([])
  const [ errorMessage, setErrorMessage ] = useState<string | null>(null)
  const [ applyError, setApplyError ] = useState<string | null>(null)

  const [ uploadDocument, uploadState ] = useUploadOrderDocumentMutation()
  const [ extractDocument, extractState ] = useExtractOrderDocumentMutation()
  const [ fetchDocument ] = useLazyOrderDocumentByIdQuery()
  const [ applyDraft, applyState ] = useApplyOrderDocumentDraftMutation()

  const resetState = useCallback(() => {
    setStep('upload')
    setSelectedFile(null)
    setDocumentId(null)
    setInvoiceDraft({})
    setProductsDraft([])
    setWarnings([])
    setErrorMessage(null)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      resetState()
    }
  }, [ isOpen, resetState ])

  const readiness = useMemo(
    () => buildReadiness(invoiceDraft, productsDraft, draftDocKind, true),
    [ draftDocKind, invoiceDraft, productsDraft ],
  )

  const applyDraftFromDocument = (document: CalculationDocument) => {
    const draft = document.draft
    if (!draft) {
      setErrorMessage(t('calc__text__documentImport__emptyDraft'))
      setStep('error')
      return
    }
    if (document.extraction_status === 'failed') {
      setErrorMessage(t('calc__text__documentImport__extractFailed'))
      setStep('error')
      return
    }
    setDraftDocKind(draft.doc_kind || document.doc_kind)
    setInvoiceDraft(draft.invoice || {})
    setProductsDraft(draft.products?.length ? draft.products.map(normalizeDraftProduct) : [])
    setWarnings(draft.warnings || [])
    setStep('edit')
  }

  const runImport = async () => {
    if (!orderId || !selectedFile) {
      return
    }
    setErrorMessage(null)
    setStep('processing')
    try {
      const uploaded = await uploadDocument({
        orderId,
        file: selectedFile,
        doc_kind: docKind,
      }).unwrap()
      setDocumentId(uploaded.id)
      dispatch(calcApi.endpoints.orderDocumentsByOrderId.initiate(orderId, { forceRefetch: true }))
      await extractDocument({
        orderId,
        documentId: uploaded.id,
        doc_kind: docKind,
      }).unwrap()
      const finalDocument = await pollOrderDocumentUntilReady(
        (args) => fetchDocument(args).unwrap(),
        orderId,
        uploaded.id,
      )
      applyDraftFromDocument(finalDocument)
    } catch (error: unknown) {
      const data = (error as { data?: Record<string, unknown> })?.data
      if (data?.code === 'draft_incomplete') {
        setErrorMessage(t('calc__text__documentImport__extractFailed'))
      } else {
        setErrorMessage(t('calc__text__documentImport__uploadFailed'))
      }
      setStep('error')
    }
  }

  const handleApply = async () => {
    if (!orderId || !documentId || !readiness.ready) {
      return
    }
    setApplyError(null)
    try {
      const applyResult = await applyDraft({
        orderId,
        documentId,
        invoice: invoiceDraft,
        products: productsDraft,
      }).unwrap()
      await refetchOrderAfterDocumentApply(orderId, applyResult.invoice.id)(dispatch)
      onApplied?.()
      onClose()
    } catch (error: unknown) {
      const data = (error as { data?: Record<string, unknown> })?.data
      if (data?.code === 'draft_incomplete') {
        setApplyError(t('calc__text__documentImport__notReady'))
        return
      }
      setApplyError(t('calc__text__documentImport__applyFailed'))
    }
  }

  const setPackagingMode = (index: number, mode: 'common' | 'per_package') => {
    setProductsDraft((prev) => prev.map((row, rowIndex) => (
      rowIndex === index ? { ...row, packaging_mode: mode } : row
    )))
  }

  const setPriceMode = (index: number, mode: 'unit_price' | 'price') => {
    setProductsDraft((prev) => prev.map((row, rowIndex) => (
      rowIndex === index ? { ...row, price_input_mode: mode } : row
    )))
  }

  const patchProduct = (index: number, patch: Partial<CalculationDocumentDraftProduct>) => {
    setProductsDraft((prev) => prev.map((row, rowIndex) => (
      rowIndex === index ? { ...row, ...patch } : row
    )))
  }

  const updateProduct = (index: number, field: keyof CalculationDocumentDraftProduct, value: string) => {
    setProductsDraft((prev) => prev.map((row, rowIndex) => {
      if (rowIndex !== index) {
        return row
      }
      const parsed = parseProductFieldValue(field, value)
      return { ...row, [field]: parsed }
    }))
  }

  const renderUploadStep = () => (
    <Box className='document-import__upload'>
      <FormControl fullWidth size='small' sx={{ mb: 2 }}>
        <InputLabel id='document-import-kind-label'>{t('calc__text__documentImport__docType')}</InputLabel>
        <Select
          labelId='document-import-kind-label'
          value={docKind}
          label={t('calc__text__documentImport__docType')}
          onChange={(event) => setDocKind(event.target.value as CalculationDocumentDocKind)}
        >
          <MenuItem value='invoice'>{t('calc__text__documentImport__typeInvoice')}</MenuItem>
          <MenuItem value='packing'>{t('calc__text__documentImport__typePacking')}</MenuItem>
        </Select>
      </FormControl>
      <input
        ref={fileInputRef}
        type='file'
        hidden
        accept={ACCEPT}
        onChange={(event) => {
          const file = event.target.files?.[0] || null
          event.target.value = ''
          setSelectedFile(file)
        }}
      />
      <Button variant='outlined' onClick={() => fileInputRef.current?.click()}>
        {t('calc__text__documentImport__chooseFile')}
      </Button>
      {selectedFile && (
        <Typography variant='body2' sx={{ mt: 1, color: COLORS.LIGHT_TEXT_SECONDARY }}>
          {selectedFile.name}
        </Typography>
      )}
      <Typography variant='caption' sx={{ display: 'block', mt: 2, color: COLORS.LIGHT_TEXT_SECONDARY }}>
        {t('calc__text__documentImport__formatsHint')}
      </Typography>
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button onClick={onClose}>{t('actions__text__close')}</Button>
        <Button
          variant='contained'
          disabled={!selectedFile || uploadState.isLoading || extractState.isLoading}
          onClick={runImport}
        >
          {t('calc__text__documentImport__start')}
        </Button>
      </Box>
    </Box>
  )

  const renderProcessingStep = () => (
    <Box className='document-import__processing'>
      <Typography variant='body1' sx={{ mb: 2 }}>
        {t('calc__text__documentImport__processing')}
      </Typography>
      <LinearProgress />
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={32} />
      </Box>
    </Box>
  )

  const renderEditStep = () => (
    <Box className='document-import__edit'>
      {applyError && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setApplyError(null)}>
          {applyError}
        </Alert>
      )}
      {!readiness.ready && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {t('calc__text__documentImport__fillRequired')}
        </Alert>
      )}
      {warnings.length > 0 && (
        <Alert severity='info' sx={{ mb: 2 }}>
          {warnings.join('\n')}
        </Alert>
      )}
      <Typography variant='subtitle2' sx={{ mb: 1 }}>
        {t('calc__text__main__invoiceDetails')}
      </Typography>
      <Box className='document-import__invoice-row'>
        <TextField
          size='small'
          label={t('calc__text__main__numOfInvoice')}
          value={invoiceDraft.invoice_num || ''}
          error={readiness.invoice_missing_fields.includes('invoice_num')}
          onChange={(event) => setInvoiceDraft((prev) => ({ ...prev, invoice_num: event.target.value }))}
        />
        <TextField
          size='small'
          type='date'
          label={t('calc__text__main__dateOfInvoice')}
          InputLabelProps={{ shrink: true }}
          value={invoiceDraft.invoice_date || ''}
          error={readiness.invoice_missing_fields.includes('invoice_date')}
          onChange={(event) => setInvoiceDraft((prev) => ({ ...prev, invoice_date: event.target.value }))}
        />
        <TextField
          size='small'
          label={t('calc__text__main__incoterms')}
          value={invoiceDraft.incoterms || ''}
          onChange={(event) => setInvoiceDraft((prev) => ({ ...prev, incoterms: event.target.value }))}
        />
      </Box>
      <TextField
        size='small'
        fullWidth
        sx={{ mt: 1.5 }}
        label={t('calc__text__main__cityOfDeliveryExplanation')}
        value={invoiceDraft.location_name || ''}
        onChange={(event) => setInvoiceDraft((prev) => ({
          ...prev,
          location_name: event.target.value,
          location: null,
        }))}
      />
      <TextField
        size='small'
        fullWidth
        sx={{ mt: 1.5 }}
        label={t('calc__text__main__descriptionOfTheGoods')}
        value={invoiceDraft.description || ''}
        onChange={(event) => setInvoiceDraft((prev) => ({ ...prev, description: event.target.value }))}
      />
      <Typography variant='subtitle2' sx={{ mt: 3, mb: 1 }}>
        {t('calc__text__documentImport__products')}
      </Typography>
      {draftDocKind === 'invoice' && (
        <Typography variant='caption' sx={{ display: 'block', mb: 2, color: COLORS.LIGHT_TEXT_SECONDARY }}>
          {t('calc__text__documentImport__packagingOptionalHint')}
        </Typography>
      )}
      <div className='document-import__products'>
        {productsDraft.map((row, index) => (
          <DocumentImportProductEditor
            key={index}
            index={index}
            row={row}
            readiness={readiness}
            showPackagingRequired={draftDocKind === 'packing'}
            onChange={updateProduct}
            onPatch={patchProduct}
            onPackagingModeChange={setPackagingMode}
            onPriceModeChange={setPriceMode}
          />
        ))}
      </div>
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button onClick={onClose}>{t('actions__text__close')}</Button>
        <Button
          variant='contained'
          disabled={!readiness.ready || applyState.isLoading}
          onClick={handleApply}
        >
          {t('calc__text__documentImport__saveToCalculation')}
        </Button>
      </Box>
    </Box>
  )

  let body: React.ReactNode = null
  if (step === 'upload') {
    body = renderUploadStep()
  } else if (step === 'processing') {
    body = renderProcessingStep()
  } else if (step === 'edit') {
    body = renderEditStep()
  } else {
    body = (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>{errorMessage}</Alert>
        <Button variant='contained' onClick={resetState}>{t('calc__text__documentImport__retry')}</Button>
      </Box>
    )
  }

  return (
    <ModalBig
      isOpen={isOpen}
      onClose={onClose}
      title={t('calc__text__documentImport__title')}
      width={1100}
    >
      {body}
    </ModalBig>
  )
}

export default DocumentImportModal
